/**
 * Client-side video (MP4/WebM) generator for a gift takeover.
 *
 * Same visual pipeline as `generateGiftGif` — samples the tier's Lottie
 * frame-by-frame, composites the branded chrome, and appends a promo
 * end-frame — but pipes the composite canvas through `MediaRecorder`
 * so the output is a shareable video file with alpha-blended text and
 * actual motion, not a GIF.
 *
 * Chrome / Firefox / Android → `video/webm` (VP9 or VP8 fallback)
 * Safari 14.1+                → `video/mp4` (H.264)
 *
 * The Web Share API on iOS/Android accepts both, so we let the browser
 * pick the mime it can produce and just label the file with the matching
 * extension.
 */
import lottie from "lottie-web";

const SIZE = 500;
const FRAME_MS = 120;         // one sampled Lottie frame every 120ms
const ANIMATION_FRAMES = 24;   // 24 * 120ms = 2.88s of animation
const END_FRAME_MS = 1400;    // hold the CTA card for 1.4s at the end
const CAPTURE_FPS = 30;

/**
 * Same helper as the GIF version — mirrors those tradeoffs for very
 * heavy tier Lotties so mobile Chrome doesn't OOM.
 */
function deriveEncodeParams(lottieData) {
  const layers = (lottieData && lottieData.layers) || [];
  const assets = (lottieData && lottieData.assets) || [];
  const isHeavy = layers.length >= 60 || assets.length >= 15;
  return {
    frames: isHeavy ? 12 : ANIMATION_FRAMES,
    size: isHeavy ? 400 : SIZE,
    frameMs: isHeavy ? 160 : FRAME_MS,
  };
}

// Choose the best MIME the browser can actually produce. Order matters:
// mp4 first for cross-platform share, then webm codecs by quality.
function pickMime() {
  const candidates = [
    "video/mp4;codecs=avc1.42E01E",       // H.264 Baseline (Safari, some Android Chrome)
    "video/mp4",
    "video/webm;codecs=vp9",
    "video/webm;codecs=vp8",
    "video/webm",
  ];
  if (typeof MediaRecorder === "undefined" || !MediaRecorder.isTypeSupported) {
    return { mime: "video/webm", ext: "webm" };
  }
  for (const m of candidates) {
    if (MediaRecorder.isTypeSupported(m)) {
      return { mime: m, ext: m.startsWith("video/mp4") ? "mp4" : "webm" };
    }
  }
  return { mime: "video/webm", ext: "webm" };
}

function loadImage(src) {
  return new Promise((resolve) => {
    if (!src) return resolve(null);
    const img = new Image();
    img.crossOrigin = "anonymous";
    img.onload = () => resolve(img);
    img.onerror = () => resolve(null);
    img.src = src;
  });
}

function svgToImage(svgEl, size) {
  return new Promise((resolve, reject) => {
    try {
      const clone = svgEl.cloneNode(true);
      clone.setAttribute("xmlns", "http://www.w3.org/2000/svg");
      clone.setAttribute("width", String(size));
      clone.setAttribute("height", String(size));
      const xml = new XMLSerializer().serializeToString(clone);
      const blob = new Blob([xml], { type: "image/svg+xml;charset=utf-8" });
      const url = URL.createObjectURL(blob);
      const img = new Image();
      img.onload = () => { URL.revokeObjectURL(url); resolve(img); };
      img.onerror = (e) => { URL.revokeObjectURL(url); reject(e); };
      img.src = url;
    } catch (e) {
      reject(e);
    }
  });
}

// Draw a <video>'s current frame onto a square canvas with `object-fit: cover`
// semantics so vertical/landscape clips fill the frame edge-to-edge like
// they do inside the live takeover.
function drawVideoCover(ctx, videoEl, size) {
  const vw = videoEl.videoWidth || size;
  const vh = videoEl.videoHeight || size;
  if (!vw || !vh) return;
  const scale = Math.max(size / vw, size / vh);
  const dw = vw * scale;
  const dh = vh * scale;
  ctx.drawImage(videoEl, (size - dw) / 2, (size - dh) / 2, dw, dh);
}

// Post-paint sanity check: read a small patch from the TOP-LEFT quadrant
// (which the chrome overlay never paints over — the tier subtitle starts
// at y = 0.36 * size, the label at y = 0.44) and return true if any
// channel of any pixel is materially non-zero. Cheap enough to call a
// few times per render.
function _sampleCanvasHasContent(ctx, size) {
  try {
    const w = 16;
    const h = 16;
    const x = Math.floor(size * 0.1);
    const y = Math.floor(size * 0.1);
    const data = ctx.getImageData(x, y, w, h).data;
    for (let i = 0; i < data.length; i += 4) {
      if (data[i] > 12 || data[i + 1] > 12 || data[i + 2] > 12) return true;
    }
    return false;
  } catch {
    // Tainted canvas (shouldn't happen — same-origin MP4s) — assume OK.
    return true;
  }
}

// -- Branded chrome overlay (matches paintChrome in generateGiftGif) --
function paintChrome(ctx, { tier, gift, avatarImg, size }) {
  const cx = size / 2;
  const usd = ((gift.amount_cents || 0) / 100).toFixed(2);
  const tipperName = gift.tipper_name || "someone";

  ctx.textAlign = "center";
  ctx.textBaseline = "middle";

  // Subtitle above tier name (e.g. // PETAL.DROPPED)
  ctx.fillStyle = tier.accent2 || "#FFC300";
  ctx.font = `600 ${Math.round(size * 0.026)}px "Space Mono", ui-monospace, monospace`;
  ctx.fillText(`// ${tier.subtitle || "boost.received"}`, cx, size * 0.36);

  // Tier label (e.g. BLOOM / SUPERNOVA)
  ctx.fillStyle = tier.accent || "#FF007F";
  ctx.font = `900 ${Math.round(size * 0.11)}px "Space Grotesk", system-ui, sans-serif`;
  ctx.fillText(tier.label || "BOOST", cx, size * 0.44);

  // "YOU.SENT" pill
  ctx.fillStyle = "rgba(255,255,255,0.75)";
  ctx.font = `500 ${Math.round(size * 0.023)}px "Space Mono", ui-monospace, monospace`;
  ctx.fillText("YOU.SENT", cx, size * 0.55);

  // Big amount
  ctx.fillStyle = "#FFFFFF";
  ctx.font = `900 ${Math.round(size * 0.17)}px "Space Grotesk", system-ui, sans-serif`;
  ctx.fillText(`$${usd}`, cx, size * 0.65);

  // Delivered strip
  ctx.fillStyle = tier.accent || "#FF007F";
  ctx.font = `600 ${Math.round(size * 0.024)}px "Space Mono", ui-monospace, monospace`;
  ctx.fillText(`${Math.floor((gift.amount_cents || 0) / 2)} coins → delivered`, cx, size * 0.73);

  // Tipper card (avatar + name)
  if (avatarImg) {
    const avatarSize = size * 0.09;
    const avatarX = cx - size * 0.18;
    const avatarY = size * 0.82;
    ctx.save();
    ctx.beginPath();
    ctx.rect(avatarX - avatarSize / 2, avatarY - avatarSize / 2, avatarSize, avatarSize);
    ctx.clip();
    ctx.drawImage(avatarImg, avatarX - avatarSize / 2, avatarY - avatarSize / 2, avatarSize, avatarSize);
    ctx.restore();
    ctx.strokeStyle = tier.accent || "#FF007F";
    ctx.lineWidth = 2;
    ctx.strokeRect(avatarX - avatarSize / 2, avatarY - avatarSize / 2, avatarSize, avatarSize);
    ctx.fillStyle = "#FFFFFF";
    ctx.textAlign = "left";
    ctx.font = `700 ${Math.round(size * 0.032)}px "Space Grotesk", system-ui, sans-serif`;
    ctx.fillText(tipperName, avatarX + avatarSize / 2 + 8, avatarY);
  }
}

function paintPromoFrame(ctx, { tier, gift, size }) {
  const cx = size / 2;
  const usd = ((gift.amount_cents || 0) / 100).toFixed(2);
  ctx.fillStyle = "#000";
  ctx.fillRect(0, 0, size, size);
  ctx.fillStyle = tier.accent || "#FF007F";
  ctx.textAlign = "center";
  ctx.textBaseline = "middle";
  ctx.font = `900 ${Math.round(size * 0.09)}px "Space Grotesk", system-ui, sans-serif`;
  ctx.fillText("I just got GIFTED", cx, size * 0.35);
  ctx.fillStyle = "#FFFFFF";
  ctx.font = `900 ${Math.round(size * 0.18)}px "Space Grotesk", system-ui, sans-serif`;
  ctx.fillText(`$${usd}`, cx, size * 0.52);
  ctx.fillStyle = "#FFC300";
  ctx.font = `700 ${Math.round(size * 0.045)}px "Space Grotesk", system-ui, sans-serif`;
  ctx.fillText("ios.matrixshopping.com", cx, size * 0.72);
  ctx.fillStyle = "rgba(255,255,255,0.65)";
  ctx.font = `500 ${Math.round(size * 0.028)}px "Space Mono", ui-monospace, monospace`;
  ctx.fillText("#matrixshopping · #boost", cx, size * 0.82);
}

/**
 * Main entry — returns a Promise<{ blob, ext, mime }>.
 * Same signature as `generateGiftGif` for a drop-in replacement in
 * `GiftShareBar`.
 */
export async function generateGiftMp4({
  gift,
  tier,
  lottieData,
  customVideoUrl = null,
  liveSvgSelector = '[data-testid="gift-lottie-layer"] svg',
}) {
  const { mime, ext } = pickMime();
  if (typeof MediaRecorder === "undefined") {
    throw new Error("Video recording not supported on this browser");
  }
  const { frames: encodeFrames, size: encodeSize, frameMs } = deriveEncodeParams(lottieData);

  const composite = document.createElement("canvas");
  composite.width = encodeSize;
  composite.height = encodeSize;
  const ctx = composite.getContext("2d");

  const avatarImg = await loadImage(gift.tipper_picture);

  // -----------------------------------------------------------------
  // Path A — admin uploaded MP4 for this tier. We hydrate an offscreen
  // <video> element, play it muted, and draw each frame onto the
  // composite canvas with the chrome overlaid, so the shared video
  // has the same background the viewer actually saw during the boost.
  // Record length matches the source clip's real duration (capped at
  // 12s) plus the 1.4s promo end-frame.
  // -----------------------------------------------------------------
  if (customVideoUrl) {
    const srcVideo = document.createElement("video");
    srcVideo.src = customVideoUrl;
    srcVideo.crossOrigin = "anonymous";
    // Keep the offscreen video MUTED so autoplay is allowed by the
    // browser policy on mobile Chrome/Safari without a fresh gesture.
    // `createMediaElementSource(srcVideo).connect(dest)` below bypasses
    // the element's muted flag and captures the raw decoded audio into
    // the recorded MediaStream — muting only silences default speaker
    // output, it doesn't affect the WebAudio graph.
    srcVideo.muted = true;
    srcVideo.volume = 1.0;
    srcVideo.playsInline = true;
    srcVideo.preload = "auto";
    srcVideo.style.cssText = "position:fixed;left:0;top:0;width:1px;height:1px;opacity:0.001;pointer-events:none;z-index:-1;";
    document.body.appendChild(srcVideo);

    // Wait for BOTH loadedmetadata (so `duration`, `videoWidth`/`Height`
    // are populated) AND loadeddata (so the first frame is decoded and
    // paintable via drawImage). Starting the capture loop before this
    // was producing black-background exports because the very first
    // frames the recorder saw were empty.
    await new Promise((resolve, reject) => {
      let done = false;
      const settle = () => { if (!done) { done = true; resolve(); } };
      const fail = (msg) => { if (!done) { done = true; reject(new Error(msg)); } };
      srcVideo.addEventListener("loadeddata", settle, { once: true });
      srcVideo.addEventListener("error", () => fail("source video failed to load"), { once: true });
      // 8s ceiling — a 25MB clip on a slow LTE link should still make it.
      setTimeout(() => fail("source video load timed out"), 8000);
    });

    // Now block until we actually have a decoded frame in the pipe:
    //   videoWidth > 0  → intrinsic dimensions available (metadata parsed)
    //   readyState >= 2 → HAVE_CURRENT_DATA (a frame at the current time
    //                     is decoded and can be drawn to canvas)
    // Some browsers reach `loadeddata` slightly before the first frame
    // is actually paintable, so we poll for up to 4 more seconds.
    {
      const readyDeadline = Date.now() + 4000;
      while (Date.now() < readyDeadline) {
        if (srcVideo.videoWidth > 0 && srcVideo.readyState >= 2) break;
        await new Promise((r) => setTimeout(r, 50));
      }
      if (!(srcVideo.videoWidth > 0 && srcVideo.readyState >= 2)) {
        try { srcVideo.remove(); } catch { /* noop */ }
        throw new Error("Source video never produced a decoded frame — cannot record");
      }
    }

    // Use the REAL metadata duration. Only fall back to a cap if the
    // browser still hands us NaN/Infinity/0 after loadedmetadata (very
    // rare — some fragmented MP4s served without a moov atom).
    let rawDur = srcVideo.duration;
    if (!Number.isFinite(rawDur) || rawDur <= 0) rawDur = 8;
    // Hard 12s cap so a mis-uploaded 5-minute clip doesn't produce a
    // 30MB share file.
    const captureDurationMs = Math.min(rawDur * 1000, 12000);

    // Prime the composite canvas with the first decoded frame + chrome
    // so the recorder's opening keyframe already shows the source clip.
    srcVideo.currentTime = 0;
    await new Promise((r) => setTimeout(r, 50));
    ctx.fillStyle = "#000";
    ctx.fillRect(0, 0, encodeSize, encodeSize);
    try {
      drawVideoCover(ctx, srcVideo, encodeSize);
    } catch { /* videoWidth was guarded above so this should not throw */ }
    paintChrome(ctx, { tier, gift, avatarImg, size: encodeSize });

    // ---- Build audio graph + resume the context BEFORE capture ----
    // A freshly-created AudioContext on mobile browsers starts in the
    // 'suspended' state until an explicit `.resume()` is called from a
    // user gesture. The takeover render is triggered by a tip-received
    // WS event (no gesture), so we must call resume() manually and
    // verify it enters 'running' before wiring up the recorder —
    // otherwise the captured audio track produces zero samples and the
    // exported clip is silent.
    let audioCtx = null;
    const canvasStream = composite.captureStream(CAPTURE_FPS);
    const tracks = [...canvasStream.getVideoTracks()];
    try {
      audioCtx = new (window.AudioContext || window.webkitAudioContext)();
      if (audioCtx.state !== "running") {
        try { await audioCtx.resume(); } catch { /* fall through */ }
      }
      // If we still can't get to 'running' (rare — mobile Safari without
      // a fresh gesture), abandon the audio graph so we at least ship a
      // silent video rather than throw.
      if (audioCtx.state === "running") {
        const audioSrc = audioCtx.createMediaElementSource(srcVideo);
        const audioDest = audioCtx.createMediaStreamDestination();
        // Route decoded audio to the recorder ONLY. Do not connect to
        // audioCtx.destination — the takeover's own <video> is already
        // playing audio to the user; a second connection would double it.
        audioSrc.connect(audioDest);
        const audioTracks = audioDest.stream.getAudioTracks();
        if (audioTracks.length) tracks.push(audioTracks[0]);
      } else {
        try { audioCtx.close(); } catch { /* noop */ }
        audioCtx = null;
      }
    } catch (audErr) {
      // AudioContext / createMediaElementSource failed. Ship silent.
      audioCtx = null;
    }
    const recStream = new MediaStream(tracks);

    const chunks = [];
    const recorder = new MediaRecorder(recStream, { mimeType: mime, videoBitsPerSecond: 3_500_000, audioBitsPerSecond: 128_000 });
    recorder.ondataavailable = (e) => { if (e.data && e.data.size > 0) chunks.push(e.data); };
    const stopped = new Promise((resolve, reject) => {
      recorder.onstop = () => resolve(new Blob(chunks, { type: mime }));
      recorder.onerror = (e) => reject(e.error || new Error("MediaRecorder error"));
    });
    recorder.start(100);
    await new Promise((r) => setTimeout(r, 40));

    // Start source playback and draw at 30fps.
    try { await srcVideo.play(); } catch { /* fallthrough — still sample via currentTime */ }

    // Track how many painted frames actually contained non-black pixels
    // from the source video (measured by sampling a 16x16 patch at the
    // canvas center). If we finish the loop with ZERO real frames, we
    // know the recorder captured a black export and we should throw
    // rather than silently ship it to the share sheet.
    let framesWithVideoContent = 0;

    const start = performance.now();
    while (true) {
      const elapsed = performance.now() - start;
      if (elapsed >= captureDurationMs) break;
      // If playback ended early (short clips), loop back so recording
      // still fills the target duration cleanly.
      if (srcVideo.ended) {
        try { srcVideo.currentTime = 0; await srcVideo.play(); } catch { /* ignore */ }
      }
      ctx.fillStyle = "#000";
      ctx.fillRect(0, 0, encodeSize, encodeSize);
      let drew = false;
      try {
        if (srcVideo.videoWidth > 0 && srcVideo.readyState >= 2) {
          drawVideoCover(ctx, srcVideo, encodeSize);
          drew = true;
        }
      } catch { /* keep black */ }
      paintChrome(ctx, { tier, gift, avatarImg, size: encodeSize });
      // Sanity sample — cheap enough to run on every ~5th frame.
      if (drew && framesWithVideoContent < 3) {
        const sample = _sampleCanvasHasContent(ctx, encodeSize);
        if (sample) framesWithVideoContent += 1;
      }
      // ~33ms between paints → 30fps into captureStream
      await new Promise((r) => setTimeout(r, 1000 / CAPTURE_FPS));
    }

    // Promo/CTA end frame — hold for END_FRAME_MS
    paintPromoFrame(ctx, { tier, gift, size: encodeSize });
    await new Promise((r) => setTimeout(r, END_FRAME_MS));

    recorder.stop();
    try { srcVideo.pause(); } catch { /* noop */ }
    try { srcVideo.remove(); } catch { /* noop */ }
    if (audioCtx) { try { audioCtx.close(); } catch { /* noop */ } }

    // If every single frame we painted was pure black in the sample
    // region, the recorder captured a blank video — better to throw a
    // clear error than let the user share a broken clip.
    if (framesWithVideoContent === 0) {
      throw new Error("Encoder produced a black background — source video failed to decode into the canvas");
    }

    const blob = await Promise.race([
      stopped,
      new Promise((_, reject) => setTimeout(() => reject(new Error("Video render timed out")), 25000)),
    ]);
    return { blob, ext, mime };
  }

  // -----------------------------------------------------------------
  // Path B — no custom MP4, fall back to Lottie SVG sampling. Same
  // pipeline as the original GIF encoder.
  // -----------------------------------------------------------------

  // Spin up an offscreen Lottie only if the live SVG isn't in the DOM.
  const liveSvg = document.querySelector(liveSvgSelector);
  let offscreenAnim = null;
  let offscreenContainer = null;
  if (!liveSvg && lottieData) {
    offscreenContainer = document.createElement("div");
    offscreenContainer.style.cssText = `position:fixed;left:0;top:0;width:${SIZE}px;height:${SIZE}px;opacity:0.001;pointer-events:none;z-index:-1;`;
    document.body.appendChild(offscreenContainer);
    try {
      offscreenAnim = lottie.loadAnimation({
        container: offscreenContainer,
        renderer: "svg",
        loop: false,
        autoplay: true,
        animationData: lottieData,
        rendererSettings: { preserveAspectRatio: "xMidYMid meet" },
      });
      await new Promise((resolve) => {
        let done = false;
        const settle = () => { if (!done) { done = true; resolve(); } };
        offscreenAnim.addEventListener("DOMLoaded", settle);
        offscreenAnim.addEventListener("data_ready", settle);
        setTimeout(settle, 1500);
      });
      await new Promise((r) => setTimeout(r, 300));
    } catch { offscreenAnim = null; }
  }
  const getSvg = () => document.querySelector(liveSvgSelector)
    || offscreenContainer?.querySelector("svg")
    || null;

  // Prime the canvas with the first frame so MediaRecorder doesn't start
  // with a black key-frame. Also renders faster on Safari.
  ctx.fillStyle = "#000";
  ctx.fillRect(0, 0, encodeSize, encodeSize);
  const primeSvg = getSvg();
  if (primeSvg) {
    try {
      const img = await svgToImage(primeSvg, encodeSize);
      const scale = Math.max(encodeSize / img.width, encodeSize / img.height);
      const dw = img.width * scale;
      const dh = img.height * scale;
      ctx.drawImage(img, (encodeSize - dw) / 2, (encodeSize - dh) / 2, dw, dh);
    } catch { /* keep black */ }
  }
  paintChrome(ctx, { tier, gift, avatarImg, size: encodeSize });

  // captureStream fires as long as the canvas keeps being drawn to.
  const stream = composite.captureStream(CAPTURE_FPS);
  const chunks = [];
  const recorder = new MediaRecorder(stream, { mimeType: mime, videoBitsPerSecond: 3_500_000 });
  recorder.ondataavailable = (e) => { if (e.data && e.data.size > 0) chunks.push(e.data); };

  const stopped = new Promise((resolve, reject) => {
    recorder.onstop = () => resolve(new Blob(chunks, { type: mime }));
    recorder.onerror = (e) => reject(e.error || new Error("MediaRecorder error"));
  });

  // Give the recorder ~40ms to latch onto the first frame before we
  // start advancing — Safari drops the leading frame otherwise.
  recorder.start(100);
  await new Promise((r) => setTimeout(r, 40));

  // Animation loop — sample the Lottie at frameMs intervals. Between
  // samples we repaint the same canvas at 30fps so captureStream has a
  // steady stream of frames (otherwise the recorder emits a stuttery clip).
  for (let i = 0; i < encodeFrames; i++) {
    ctx.fillStyle = "#000";
    ctx.fillRect(0, 0, encodeSize, encodeSize);
    const svgEl = getSvg();
    if (svgEl) {
      try {
        const img = await svgToImage(svgEl, encodeSize);
        const scale = Math.max(encodeSize / img.width, encodeSize / img.height);
        const dw = img.width * scale;
        const dh = img.height * scale;
        ctx.drawImage(img, (encodeSize - dw) / 2, (encodeSize - dh) / 2, dw, dh);
      } catch { /* keep black */ }
    }
    paintChrome(ctx, { tier, gift, avatarImg, size: encodeSize });
    await new Promise((r) => setTimeout(r, frameMs));
  }

  // Promo/CTA end frame — hold for END_FRAME_MS so the recorder captures it.
  paintPromoFrame(ctx, { tier, gift, size: encodeSize });
  await new Promise((r) => setTimeout(r, END_FRAME_MS));

  recorder.stop();
  // Cleanup offscreen resources
  if (offscreenAnim) { try { offscreenAnim.destroy(); } catch { /* noop */ } }
  if (offscreenContainer) { try { offscreenContainer.remove(); } catch { /* noop */ } }

  // Hard timeout — mirror the GIF encoder's safety net.
  const blob = await Promise.race([
    stopped,
    new Promise((_, reject) => setTimeout(() => reject(new Error("Video render timed out")), 20000)),
  ]);
  return { blob, ext, mime };
}
